import React, { useEffect, useState } from "react";

import ChoiceCard from "../components/ChoiceCard.jsx";
import ChoiceFeatModal from "../components/ChoiceFeatModal.jsx";
import SearchBar from "../components/SearchBar.jsx";
import {
  ABILITY_KEYS,
  ABILITY_NAMES,
  ALL_SKILLS,
  ALL_TOOLS,
  buildBackgroundStats,
  getBackgroundAbilityKeys,
} from "../characterCreatorUtils.js";

const CUSTOM_BG_ENTRY = {
  name: "Custom Background",
  isCustom: true,
  summary: "Crie seu próprio background com perícias, ferramenta, feat e equipamento à sua escolha.",
  benefits: [
    "Atributos: Força, Destreza, Constituição, Inteligência, Sabedoria, Carisma",
    "Talento de Origem: à escolha",
    "Perícias: 2 à escolha",
    "Ferramenta: 1 à escolha",
    "Equipamento: 50 GP + itens customizados",
  ],
  image: "assets/images/cards/default-fantasy.svg",
};

const EMPTY_BG_STATS = { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 };

function AbilityBonusSelector({
  abilityKeys,
  mode,
  plusTwo,
  plusOne,
  plusOnes,
  onSetMode,
  onAssignTwoOne,
  onAssignOnes,
  showTashaToggle,
  tashaEnabled,
  onToggleTasha,
}) {
  if (abilityKeys.length === 0) return null;

  const needsTripleSelection = mode === "1-1-1" && abilityKeys.length > 3;

  function handleClickAbility(key) {
    if (plusTwo === key) {
      // Clear plusTwo; if plusOne exists, promote it
      onAssignTwoOne({ plusTwo: plusOne || null, plusOne: null });
    } else if (plusOne === key) {
      onAssignTwoOne({ plusTwo, plusOne: null });
    } else if (!plusTwo) {
      onAssignTwoOne({ plusTwo: key, plusOne });
    } else if (!plusOne) {
      onAssignTwoOne({ plusTwo, plusOne: key });
    } else {
      // Both assigned: replace plusOne with clicked key
      onAssignTwoOne({ plusTwo, plusOne: key });
    }
  }

  function handleTogglePlusOne(key) {
    if (plusOnes.includes(key)) {
      onAssignOnes(plusOnes.filter((k) => k !== key));
      return;
    }
    if (plusOnes.length < 3) {
      onAssignOnes([...plusOnes, key]);
    }
  }

  return (
    <div className="bg-attr-selector">
      <div className="bg-attr-head">
        <div>
          <div className="bg-attr-title">Distribuição de Atributos</div>
          <div className="bg-attr-desc">
            Escolha como distribuir os bônus deste background nos atributos permitidos.
          </div>
        </div>

        {showTashaToggle && (
          <label className="bg-tasha-toggle">
            <input
              type="checkbox"
              checked={tashaEnabled}
              onChange={(e) => onToggleTasha(e.target.checked)}
            />
            <span>Regra de Tasha?</span>
          </label>
        )}
      </div>

      <div className="bg-attr-modes">
        <button
          type="button"
          className={`bg-mode-btn${mode === "2-1" ? " bg-mode-btn--active" : ""}`}
          onClick={() => onSetMode("2-1")}
        >
          <span className="bg-mode-label">▲▲ + ▲</span>
          <span className="bg-mode-desc">+2 em um, +1 em outro</span>
        </button>
        <button
          type="button"
          className={`bg-mode-btn${mode === "1-1-1" ? " bg-mode-btn--active" : ""}`}
          onClick={() => onSetMode("1-1-1")}
        >
          <span className="bg-mode-label">▲ + ▲ + ▲</span>
          <span className="bg-mode-desc">+1 nos três</span>
        </button>
      </div>

      {mode === "2-1" && (
        <div className="bg-attr-assign">
          {abilityKeys.map((key) => {
            const isPlus2 = plusTwo === key;
            const isPlus1 = plusOne === key;
            const valLabel = isPlus2 ? "+2" : isPlus1 ? "+1" : "+0";
            const cls = `bg-attr-btn${isPlus2 ? " bg-attr-btn--plus2" : isPlus1 ? " bg-attr-btn--plus1" : ""}`;
            return (
              <button key={key} type="button" className={cls} onClick={() => handleClickAbility(key)}>
                <span className="bg-attr-name">{ABILITY_NAMES[key]}</span>
                <span className="bg-attr-val">{valLabel}</span>
              </button>
            );
          })}
          <div className="bg-attr-hint">
            {!plusTwo
              ? "Clique em um atributo para receber +2"
              : !plusOne
              ? "Clique em outro para receber +1"
              : "✓ Distribuição definida"}
          </div>
        </div>
      )}

      {mode === "1-1-1" && (
        needsTripleSelection ? (
          <div className="bg-attr-assign">
            {abilityKeys.map((key) => {
              const isPlus1 = plusOnes.includes(key);
              const valLabel = isPlus1 ? "+1" : "+0";
              const cls = `bg-attr-btn${isPlus1 ? " bg-attr-btn--plus1" : ""}`;
              return (
                <button
                  key={key}
                  type="button"
                  className={cls}
                  onClick={() => handleTogglePlusOne(key)}
                  disabled={!isPlus1 && plusOnes.length >= 3}
                >
                  <span className="bg-attr-name">{ABILITY_NAMES[key]}</span>
                  <span className="bg-attr-val">{valLabel}</span>
                </button>
              );
            })}
            <div className="bg-attr-hint">
              {plusOnes.length < 3
                ? `Selecione ${3 - plusOnes.length} atributo(s) para receber +1`
                : "✓ Distribuição definida"}
            </div>
          </div>
        ) : (
          <div className="bg-attr-confirm">
            {abilityKeys.map((key) => (
              <span key={key} className="bg-attr-tag bg-attr-tag--plus1">
                {ABILITY_NAMES[key]} +1
              </span>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ── CUSTOM BACKGROUND FORM ────────────────────────────────────

function CustomBackgroundForm({ customBackground, onOpenOriginFeatModal }) {
  if (!customBackground) return null;

  const selectedSkills = (customBackground.skills || []).filter(Boolean);
  const toolLabel = customBackground.tool === "__custom"
    ? (customBackground.customTool || "Não informada")
    : (customBackground.tool || "Não selecionada");
  const equipmentLabel = customBackground.equipmentNotes
    ? `50 GP + ${customBackground.equipmentNotes}`
    : "50 GP";

  return (
    <>
      <div className="bg-detail-section">
        <div className="bg-detail-section-title">Escolhas do Background</div>
        <button
          type="button"
          className="cl-modal-trigger"
          onClick={onOpenOriginFeatModal}
        >
          {customBackground.originFeat ? "Ver ou editar escolhas" : "Definir escolhas do background"}
        </button>
        <div className="cl-choice-list">
          <div className="cl-choice-line">
            <span className="cl-choice-label">Perícias</span>
            <span className="cl-choice-value">
              {selectedSkills.length ? selectedSkills.join(", ") : "Nenhuma selecionada"}
            </span>
          </div>
          <div className="cl-choice-line">
            <span className="cl-choice-label">Ferramenta</span>
            <span className="cl-choice-value">{toolLabel}</span>
          </div>
          <div className="cl-choice-line">
            <span className="cl-choice-label">Talento</span>
            <span className="cl-choice-value">{customBackground.originFeat || "Não selecionado"}</span>
          </div>
          <div className="cl-choice-line">
            <span className="cl-choice-label">Equipamento</span>
            <span className="cl-choice-value">{equipmentLabel}</span>
          </div>
        </div>
        <span className="cl-hint">
          Todas as escolhas especiais do Custom Background ficam centralizadas neste modal.
        </span>
      </div>
    </>
  );
}

// ── BACKGROUND DETAIL ─────────────────────────────────────────

function BackgroundDetail({
  background,
  state,
  onSetBackgroundStats,
  onOpenOriginFeatModal,
}) {
  const [tashaRuleEnabled, setTashaRuleEnabled] = useState(false);
  const mode = state.backgroundStatMode || null;
  const stats = state.backgroundStats || {};
  const baseAbilityKeys = getBackgroundAbilityKeys(background);
  const abilityKeys = !background.isCustom && tashaRuleEnabled ? ABILITY_KEYS : baseAbilityKeys;

  useEffect(() => {
    setTashaRuleEnabled(false);
  }, [background.name]);

  const plusTwo = abilityKeys.find((k) => stats[k] === 2) || null;
  const plusOne = abilityKeys.find((k) => stats[k] === 1) || null;
  const plusOnes = abilityKeys.filter((k) => stats[k] === 1);

  function handleSetMode(newMode) {
    const newStats = buildBackgroundStats(abilityKeys, newMode, null, null, []);
    onSetBackgroundStats({ stats: newStats, mode: newMode });
  }

  function handleAssignTwoOne({ plusTwo: pt, plusOne: po }) {
    const newStats = buildBackgroundStats(abilityKeys, "2-1", pt, po, []);
    onSetBackgroundStats({ stats: newStats, mode: "2-1" });
  }

  function handleAssignOnes(selectedKeys) {
    const newStats = buildBackgroundStats(abilityKeys, "1-1-1", null, null, selectedKeys);
    onSetBackgroundStats({ stats: newStats, mode: "1-1-1" });
  }

  function handleToggleTasha(nextEnabled) {
    setTashaRuleEnabled(nextEnabled);
    onSetBackgroundStats({ stats: { ...EMPTY_BG_STATS }, mode: null });
  }

  return (
    <div className="bg-detail-panel">
      <div className="bg-detail-name">{background.name}</div>
      {background.summary && (
        <p className="bg-detail-summary">{background.summary}</p>
      )}

      <div className="bg-detail-section">
        <div className="bg-detail-section-title">Benefícios</div>
        <ul className="bg-detail-benefits">
          {(background.benefits || []).map((line, i) => (
            <li
              key={i}
              className={/^atributos\s*:/i.test(line) ? "bg-benefit-attr" : "bg-benefit-line"}
            >
              {line}
            </li>
          ))}
        </ul>
      </div>

      {background.isCustom && (
        <CustomBackgroundForm
          customBackground={state.customBackground}
          onOpenOriginFeatModal={onOpenOriginFeatModal}
        />
      )}

      {abilityKeys.length > 0 && (
        <AbilityBonusSelector
          abilityKeys={abilityKeys}
          mode={mode}
          plusTwo={plusTwo}
          plusOne={plusOne}
          plusOnes={plusOnes}
          onSetMode={handleSetMode}
          onAssignTwoOne={handleAssignTwoOne}
          onAssignOnes={handleAssignOnes}
          showTashaToggle={!background.isCustom && baseAbilityKeys.length > 0}
          tashaEnabled={tashaRuleEnabled}
          onToggleTasha={handleToggleTasha}
        />
      )}
    </div>
  );
}

export default function BackgroundStep({
  state,
  backgrounds,
  originFeats,
  onSelectBackground,
  onSetBackgroundStats,
  onPatchCustomBackground,
}) {
  const [query, setQuery] = useState("");
  const [isFeatModalOpen, setIsFeatModalOpen] = useState(false);

  const allBgs = Array.isArray(backgrounds) ? [CUSTOM_BG_ENTRY, ...backgrounds] : [];

  const selectedBg = allBgs.find((bg) => bg.name === state.background) || null;

  const filtered = query
    ? allBgs.filter((bg) => {
        const q = query.toLowerCase();
        return (
          bg.name.toLowerCase().includes(q) ||
          bg.summary?.toLowerCase().includes(q) ||
          bg.benefits?.some((b) => b.toLowerCase().includes(q))
        );
      })
    : allBgs;

  return (
    <div className="bg-step-layout">
      <div className="bg-cards-col">
        <SearchBar value={query} onChange={setQuery} placeholder="Buscar background…" />
        <div className="card-grid card-grid--portrait">
          {filtered.map((bg) => (
            <ChoiceCard
              key={bg.name}
              selected={state.background === bg.name}
              imageUrl={bg.image || "assets/images/cards/default-fantasy.svg"}
              imageLabel={bg.name}
              imageVertical={true}
              badge={bg.isCustom ? <span className="badge-2024">2024</span> : null}
              onClick={() => {
                if (state.background !== bg.name) {
                  onSelectBackground(bg);
                }
                if (!bg.isCustom) {
                  setIsFeatModalOpen(false);
                }
              }}
            >
              <div className="card-name">{bg.name}</div>
              {bg.summary && <div className="card-detail">{bg.summary}</div>}
            </ChoiceCard>
          ))}
          {filtered.length === 0 && query && (
            <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
              Nenhum background encontrado para &ldquo;{query}&rdquo;.
            </p>
          )}
        </div>
      </div>

      <div className="bg-info-col">
        {selectedBg ? (
          <BackgroundDetail
            background={selectedBg}
            state={state}
            onSetBackgroundStats={onSetBackgroundStats}
            onOpenOriginFeatModal={() => setIsFeatModalOpen(true)}
          />
        ) : (
          <div className="bg-empty-panel">
            <div className="bg-empty-icon">📖</div>
            <div className="bg-empty-text">
              Selecione um background para ver os detalhes e distribuir os bônus de atributos.
            </div>
          </div>
        )}
      </div>

      <ChoiceFeatModal
        isOpen={isFeatModalOpen && state.background === "Custom Background"}
        mode="background-custom"
        skills={ALL_SKILLS}
        tools={ALL_TOOLS}
        feats={originFeats || []}
        initialSelection={{
          skills: state.customBackground?.skills || ["", ""],
          tool: state.customBackground?.tool || "",
          customTool: state.customBackground?.customTool || "",
          feat: state.customBackground?.originFeat || "",
          equipmentNotes: state.customBackground?.equipmentNotes || "",
        }}
        onClose={() => setIsFeatModalOpen(false)}
        onConfirm={(selection) => {
          onPatchCustomBackground({
            skills: selection.skills || ["", ""],
            tool: selection.tool || "",
            customTool: selection.customTool || "",
            originFeat: selection.feat || "",
            equipmentNotes: selection.equipmentNotes || "",
          });
          setIsFeatModalOpen(false);
        }}
      />
    </div>
  );
}
